import { useState } from "react";
import { Link } from "react-router-dom";
import { useModal } from "@/stores/modal";
import { cx } from "class-variance-authority";
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  Settings,
  BarChart3,
  Download,
  Upload,
  Eye,
  Plus,
} from "lucide-react";

import SidebarChevron from "@/assets/icons/SidebarChevron";
import ExportSOT from "@/pages/SD/EXPORT/ExportSOT";
import ExportPRT from "@/pages/SD/EXPORT/ExportPRT";
import ExportMachine from "@/pages/SD/EXPORT/ExportMachine";
import ImportSOT from "@/pages/SD/EXPORT/ImportSOT";
import ExportUptime from "@/pages/SD/EXPORT/ExportUptime";
import {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
} from "@/components/ui/dropdown";

export default function SidebarSD({ sidebarMinMax = false }) {
  const [op, setOp] = useState(false);
  const [sot, setSot] = useState(false);
  const [SDpr, setSDpr] = useState(false);
  const [reportingMenu, setReportingMenu] = useState(false);
  const [dashboardMenu, setDashboardMenu] = useState(false);
  const { open } = useModal();
  const handleExportSOT = () => {
    open({
      html: <ExportSOT />,
      width: "500",
      state: true,
      position: "center",
    });
  };
  const handleExportCMMachine = () => {
    open({
      html: <ExportMachine />,
      width: "500",
      state: true,
      position: "center",
    });
  };
  const handleImportSOT = () => {
    open({
      html: <ImportSOT />,
      width: "500",
      state: true,
      position: "center",
    });
  };
  const handleExportPRT = () => {
    open({
      html: <ExportPRT key={Date.now()} />,
      width: "500",
      state: true,
      position: "center",
    });
  };

  const handleExportUptime = () => {
    open({
      html: <ExportUptime />,
      width: "500",
      state: true,
      position: "center",
    });
  };

  // Helper function to handle accordion behavior - close all other groups when one is opened
  const handleGroupToggle = (groupSetter, currentValue) => {
    // Close all groups
    setOp(false);
    setSot(false);
    setSDpr(false);
    setReportingMenu(false);
    setDashboardMenu(false);
    // Toggle the target group
    groupSetter(!currentValue);
  };
  return (
    <>
      {sidebarMinMax ? (
        <Dropdown>
          <DropdownTrigger>
            <button
              className={cx(
                "w-full",
                "flex",
                "items-center",
                "justify-center",
                "px-4",
                "py-3",
                "rounded-lg",
                "text-gray-700",
                "hover:bg-gray-100",
                "transition-all",
                "duration-200",
                "group",
              )}
            >
              <LayoutDashboard className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            </button>
          </DropdownTrigger>
          <DropdownContent>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] mx-2 my-1">
              <DropdownItem>
                <Link
                  to={"/service-desk/dashboard"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard SLM
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link
                  to={"/service-desk/dashboard/first-level"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard FLM
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link
                  to={"/service-desk/dashboard/cash-in-transit"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard CIT
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link
                  to={"/service-desk/dashboard/comm-vendor"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard CV
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link
                  to={"/service-desk/dashboard/analytics"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <BarChart3 className="w-4 h-4" />
                  Dashboard Analytics
                </Link>
              </DropdownItem>
            </div>
          </DropdownContent>
        </Dropdown>
      ) : (
        <>
          <button
            className={cx(
              "w-full",
              "flex",
              "items-center",
              "justify-between",
              "px-4",
              "py-3",
              "rounded-lg",
              "text-gray-700",
              "hover:bg-gray-100",
              "transition-all",
              "duration-200",
              "group",
            )}
            onClick={() => handleGroupToggle(setDashboardMenu, dashboardMenu)}
          >
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              <span className="font-medium">Dashboard</span>
            </div>
            <SidebarChevron />
          </button>
          {dashboardMenu && (
            <div className={`flex flex-col px-[20px] gap-[20px]`}>
              <Link
                to={"/service-desk/dashboard"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard SLM
              </Link>
              <Link
                to={"/service-desk/dashboard/first-level"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard FLM
              </Link>
              <Link
                to={"/service-desk/dashboard/cash-in-transit"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard CIT
              </Link>
              <Link
                to={"/service-desk/dashboard/comm-vendor"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard CV
              </Link>
              <Link
                to={"/service-desk/dashboard/analytics"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <BarChart3 className="w-4 h-4" />
                Dashboard Analytics
              </Link>
            </div>
          )}
        </>
      )}
      {sidebarMinMax ? (
        <Dropdown>
          <DropdownTrigger>
            <button
              className={cx(
                "w-full",
                "flex",
                "items-center",
                "justify-center",
                "px-4",
                "py-3",
                "rounded-lg",
                "text-gray-700",
                "hover:bg-gray-100",
                "transition-all",
                "duration-200",
                "group",
              )}
            >
              <FileText className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            </button>
          </DropdownTrigger>
          <DropdownContent>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] mx-2 my-1">
              <DropdownItem>
                <Link
                  to={"/service-desk/sot/all"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <Eye className="w-4 h-4" />
                  All
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link
                  to={"/service-desk/sot/create"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Create
                </Link>
              </DropdownItem>
              <DropdownItem>
                <button
                  onClick={handleImportSOT}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 text-left w-full"
                >
                  <Upload className="w-4 h-4" />
                  Import
                </button>
              </DropdownItem>
            </div>
          </DropdownContent>
        </Dropdown>
      ) : (
        <>
          <button
            className={cx(
              "w-full",
              "flex",
              "items-center",
              "justify-between",
              "px-4",
              "py-3",
              "rounded-lg",
              "text-gray-700",
              "hover:bg-gray-100",
              "transition-all",
              "duration-200",
              "group",
            )}
            onClick={() => handleGroupToggle(setSot, sot)}
          >
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              <span className="font-medium">Service Order</span>
            </div>
            <SidebarChevron />
          </button>
          {sot && (
            <div className={`flex flex-col px-[20px] gap-[20px]`}>
              <Link
                to={"/service-desk/sot/all"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <Eye className="w-4 h-4" />
                All
              </Link>
              <Link
                to={"/service-desk/sot/create"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <Plus className="w-4 h-4" />
                Create
              </Link>
              <button
                onClick={handleImportSOT}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                  "text-left",
                )}
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
            </div>
          )}
        </>
      )}
      {sidebarMinMax ? (
        <Dropdown>
          <DropdownTrigger>
            <button
              className={cx(
                "w-full",
                "flex",
                "items-center",
                "justify-center",
                "px-4",
                "py-3",
                "rounded-lg",
                "text-gray-700",
                "hover:bg-gray-100",
                "transition-all",
                "duration-200",
                "group",
              )}
            >
              <Settings className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            </button>
          </DropdownTrigger>
          <DropdownContent>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] mx-2 my-1">
              <DropdownItem>
                <Link
                  to={"/service-desk/op/all"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <Eye className="w-4 h-4" />
                  All
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link
                  to={"/service-desk/op/create"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  Create
                </Link>
              </DropdownItem>
            </div>
          </DropdownContent>
        </Dropdown>
      ) : (
        <>
          <button
            className={cx(
              "w-full",
              "flex",
              "items-center",
              "justify-between",
              "px-4",
              "py-3",
              "rounded-lg",
              "text-gray-700",
              "hover:bg-gray-100",
              "transition-all",
              "duration-200",
              "group",
            )}
            onClick={() => handleGroupToggle(setOp, op)}
          >
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              <span className="font-medium">Checklist Setting</span>
            </div>
            <SidebarChevron />
          </button>
          {op && (
            <div className={`flex flex-col px-[20px] gap-[20px]`}>
              <Link
                to={"/service-desk/op/all"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <Eye className="w-4 h-4" />
                All
              </Link>
              <Link
                to={"/service-desk/op/create"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <Plus className="w-4 h-4" />
                Create
              </Link>
            </div>
          )}
        </>
      )}
      {sidebarMinMax ? (
        <Dropdown>
          <DropdownTrigger>
            <button
              className={cx(
                "w-full",
                "flex",
                "items-center",
                "justify-center",
                "px-4",
                "py-3",
                "rounded-lg",
                "text-gray-700",
                "hover:bg-gray-100",
                "transition-all",
                "duration-200",
                "group",
              )}
            >
              <ClipboardList className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            </button>
          </DropdownTrigger>
          <DropdownContent>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] mx-2 my-1">
              <DropdownItem>
                <Link
                  to={"/service-desk/pr/all"}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
                >
                  <Eye className="w-4 h-4" />
                  All
                </Link>
              </DropdownItem>
              <DropdownItem>
                <button
                  onClick={handleExportPRT}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 text-left w-full"
                >
                  <Download className="w-4 h-4" />
                  Export PRT
                </button>
              </DropdownItem>
            </div>
          </DropdownContent>
        </Dropdown>
      ) : (
        <>
          <button
            className={cx(
              "w-full",
              "flex",
              "items-center",
              "justify-between",
              "px-4",
              "py-3",
              "rounded-lg",
              "text-gray-700",
              "hover:bg-gray-100",
              "transition-all",
              "duration-200",
              "group",
            )}
            onClick={() => handleGroupToggle(setSDpr, SDpr)}
          >
            <div className="flex items-center gap-3">
              <ClipboardList className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              <span className="font-medium">Part Request</span>
            </div>
            <SidebarChevron />
          </button>
          {SDpr && (
            <div className={`flex flex-col px-[20px] gap-[20px]`}>
              <Link
                to={"/service-desk/pr/all"}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                )}
              >
                <Eye className="w-4 h-4" />
                All
              </Link>
              <button
                onClick={handleExportPRT}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                  "text-left",
                )}
              >
                <Download className="w-4 h-4" />
                Export PRT
              </button>
            </div>
          )}
        </>
      )}
      {sidebarMinMax ? (
        <Dropdown>
          <DropdownTrigger>
            <button
              className={cx(
                "w-full",
                "flex",
                "items-center",
                "justify-center",
                "px-4",
                "py-3",
                "rounded-lg",
                "text-gray-700",
                "hover:bg-gray-100",
                "transition-all",
                "duration-200",
                "group",
              )}
            >
              <BarChart3 className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            </button>
          </DropdownTrigger>
          <DropdownContent>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] mx-2 my-1">
              <DropdownItem>
                <button
                  onClick={handleExportSOT}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 text-left w-full"
                >
                  <Download className="w-4 h-4" />
                  Ticket Detail Report
                </button>
              </DropdownItem>
              <DropdownItem>
                <button
                  onClick={handleExportCMMachine}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 text-left w-full"
                >
                  <Download className="w-4 h-4" />
                  Ticket Summary Report
                </button>
              </DropdownItem>
              <DropdownItem>
                <button
                  onClick={handleExportUptime}
                  className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 text-left w-full"
                >
                  <Download className="w-4 h-4" />
                  SLA Report
                </button>
              </DropdownItem>
            </div>
          </DropdownContent>
        </Dropdown>
      ) : (
        <>
          <button
            className={cx(
              "w-full",
              "flex",
              "items-center",
              "justify-between",
              "px-4",
              "py-3",
              "rounded-lg",
              "text-gray-700",
              "hover:bg-gray-100",
              "transition-all",
              "duration-200",
              "group",
            )}
            onClick={() => handleGroupToggle(setReportingMenu, reportingMenu)}
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              <span className="font-medium">Reporting</span>
            </div>
            <SidebarChevron />
          </button>
          {reportingMenu && (
            <div className={`flex flex-col px-[20px] gap-[20px]`}>
              <button
                onClick={handleExportSOT}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                  "text-left",
                )}
              >
                <Download className="w-4 h-4" />
                Ticket Detail Report
              </button>
              <button
                onClick={handleExportCMMachine}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                  "text-left",
                )}
              >
                <Download className="w-4 h-4" />
                Ticket Summary Report
              </button>
              <button
                onClick={handleExportUptime}
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                  "text-left",
                )}
              >
                <Download className="w-4 h-4" />
                SLA Report
              </button>
              <Link
                to="/report/performance"
                className={cx(
                  "flex",
                  "items-center",
                  "gap-3",
                  "px-3",
                  "py-2",
                  "text-sm",
                  "text-gray-600",
                  "hover:text-blue-600",
                  "hover:bg-blue-50",
                  "rounded-md",
                  "transition-all",
                  "duration-200",
                  "text-left",
                )}
              >
                <BarChart3 className="w-4 h-4" />
                Performance Analysis
              </Link>
            </div>
          )}
        </>
      )}
    </>
  );
}
